/**
 * App startup screen preference helpers.
 *
 * Values (kept in sync with Kjuup-BE `UserController.STARTUP_SCREENS`):
 *   'cockpit'           personal start page (default)
 *   'feed'              News feed (/articles)
 *   'messages'          Latest messages
 *   'rooms'             Alle Räume
 *   ('todo' was retired 2026-10-30: tasks live inside projects and on the
 *    cockpit; stored values fall back to 'cockpit')
 *   ('profile' was retired; stored values fall back to 'cockpit')
 *   'companies'         Firmen           } retired 2026-10-16: no longer offered, stored
 *   'company-documents' Firmendokumente  } values fall back to 'cockpit'
 *
 * Chat/email `/deeplink?...` URLs must survive login. The logged-out tree,
 * 401 logout, and the startup redirect all rewrite the address bar, so the
 * incoming path is captured immediately and restored after the token is set.
 */

const STORAGE_KEY = "startupScreen";
const PENDING_PATH_KEY = "pendingStartupPath";
const PENDING_SIDEBAR_KEY = "pendingStartupSidebar";
const PENDING_DEEPLINK_KEY = "pendingDeeplink";
const PENDING_DEEPLINK_AT_KEY = "pendingDeeplinkAt";
const MAX_DEEPLINK_AGE_MS = 30 * 60 * 1000;

export const DEFAULT_STARTUP_SCREEN = "cockpit";

/**
 * Ordered list of selectable start pages.
 * `labelKey` -> Languages/*.js key, `aliases` -> paths that already count as
 * "on the startup screen" (no redirect), `sidebar` -> Redux activeSideBar.
 */
export const STARTUP_SCREENS = [
  { value: "cockpit", path: "/", aliases: ["/", "/cockpit"], sidebar: "cockpit", labelKey: "startup_screen_cockpit" },
  { value: "feed", path: "/articles", aliases: ["/articles"], sidebar: "articles", labelKey: "nav_news" },
  { value: "messages", path: "/messages", aliases: ["/messages"], sidebar: "LatestMessages", labelKey: "nav_messages" },
  { value: "rooms", path: "/projects", aliases: ["/projects"], sidebar: "project1", labelKey: "nav_all_rooms" },
  // Retired start pages stay listed only so their paths still map to a sidebar entry.
  // The old ToDo page redirects to the cockpit.
  { value: "todo", path: "/", aliases: ["/todo"], sidebar: "cockpit", labelKey: "nav_todo", retired: true },
  { value: "companies", path: "/my-companies", aliases: ["/my-companies"], sidebar: "my-companies", labelKey: "nav_company", retired: true },
  {
    value: "company-documents",
    path: "/company-documents",
    aliases: ["/company-documents"],
    sidebar: "company-documents",
    labelKey: "nav_company_documents",
    requiresCompany: true,
    retired: true,
  },
];

const SCREEN_BY_VALUE = STARTUP_SCREENS.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

export function isKnownStartupScreen(value) {
  const screen = SCREEN_BY_VALUE[String(value || "")];
  return !!screen && !screen.retired;
}

/** The start pages the settings offer. */
export const SELECTABLE_STARTUP_SCREENS = STARTUP_SCREENS.filter((s) => !s.retired);

export function resolveStartupScreen(value) {
  return isKnownStartupScreen(value) ? String(value) : DEFAULT_STARTUP_SCREEN;
}

/**
 * Route for a startup value. `options.companyId` fills the Firmendokumente
 * route; without a company the cockpit is used instead.
 */
export function getStartupRoute(value, options = {}) {
  const screen = SCREEN_BY_VALUE[resolveStartupScreen(value)] || SCREEN_BY_VALUE[DEFAULT_STARTUP_SCREEN];
  if (screen.requiresCompany) {
    const companyId = options.companyId || readPersistedCompanyId();
    if (!companyId) {
      const fallback = SCREEN_BY_VALUE[DEFAULT_STARTUP_SCREEN];
      return { path: fallback.path, sidebar: fallback.sidebar, value: fallback.value };
    }
    return { path: `${screen.path}/${companyId}`, sidebar: screen.sidebar, value: screen.value };
  }
  return { path: screen.path, sidebar: screen.sidebar, value: screen.value };
}

/** True when `pathname` already shows the startup screen `value`. */
export function isOnStartupScreen(value, pathname) {
  const screen = SCREEN_BY_VALUE[resolveStartupScreen(value)] || SCREEN_BY_VALUE[DEFAULT_STARTUP_SCREEN];
  const path = String(pathname || "");
  if (screen.requiresCompany) return path.startsWith(screen.path);
  return screen.aliases.includes(path);
}

/** Remember the company used for the Firmendokumente start page. */
export function persistStartupCompanyId(companyId) {
  try {
    if (companyId) localStorage.setItem("startupCompanyId", String(companyId));
  } catch (e) {}
}

function readPersistedCompanyId() {
  try {
    return localStorage.getItem("startupCompanyId") || null;
  } catch (e) {
    return null;
  }
}

export function persistStartupScreen(value) {
  const resolved = resolveStartupScreen(value);
  try {
    localStorage.setItem(STORAGE_KEY, resolved);
  } catch (e) {}
  return resolved;
}

export function readPersistedStartupScreen() {
  try {
    return resolveStartupScreen(localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return DEFAULT_STARTUP_SCREEN;
  }
}

export function getStartupScreenFromUser(user) {
  if (isKnownStartupScreen(user?.startupScreen)) {
    return resolveStartupScreen(user.startupScreen);
  }
  return readPersistedStartupScreen();
}

export function isDeeplinkPath(pathAndSearch) {
  if (!pathAndSearch) return false;
  const path = String(pathAndSearch).split("?")[0];
  return path === "/deeplink" || path.startsWith("/deeplink/");
}

function sidebarForPath(pathAndSearch) {
  if (isDeeplinkPath(pathAndSearch)) {
    const params = new URLSearchParams(String(pathAndSearch).split("?")[1] || "");
    return params.get("roomId") ? "project1" : "chat1";
  }
  const path = String(pathAndSearch).split("?")[0];
  const match = STARTUP_SCREENS.find((s) =>
    s.requiresCompany ? path.startsWith(s.path) : s.aliases.includes(path)
  );
  return match ? match.sidebar : SCREEN_BY_VALUE[DEFAULT_STARTUP_SCREEN].sidebar;
}

function readSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeSession(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {}
}

function readFreshStoredDeeplink() {
  try {
    const stored = localStorage.getItem(PENDING_DEEPLINK_KEY);
    const at = Number(localStorage.getItem(PENDING_DEEPLINK_AT_KEY) || 0);
    if (isDeeplinkPath(stored) && Date.now() - at < MAX_DEEPLINK_AGE_MS) {
      return stored;
    }
  } catch (e) {}
  return null;
}

export function getPendingDeeplink() {
  const queued = readSession(PENDING_PATH_KEY);
  if (isDeeplinkPath(queued)) return queued;
  return readFreshStoredDeeplink();
}

function persistDeeplink(pathAndSearch) {
  if (!isDeeplinkPath(pathAndSearch)) return null;
  try {
    localStorage.setItem(PENDING_DEEPLINK_KEY, pathAndSearch);
    localStorage.setItem(PENDING_DEEPLINK_AT_KEY, String(Date.now()));
  } catch (e) {}
  writeSession(PENDING_PATH_KEY, pathAndSearch);
  writeSession(PENDING_SIDEBAR_KEY, sidebarForPath(pathAndSearch));
  return { path: pathAndSearch, sidebar: sidebarForPath(pathAndSearch) };
}

function clearStoredDeeplink() {
  try {
    localStorage.removeItem(PENDING_DEEPLINK_KEY);
    localStorage.removeItem(PENDING_DEEPLINK_AT_KEY);
  } catch (e) {}
}

/**
 * Snapshot `/deeplink?...` from the address bar before auth/logout/startup
 * navigation can replace it. Safe to call repeatedly.
 */
export function captureIncomingDeeplink() {
  try {
    const path = window.location.pathname + (window.location.search || "");
    return persistDeeplink(path);
  } catch (e) {
    return null;
  }
}

/**
 * Queue the post-login destination before flipping auth state.
 * Login lives in a nested Router that unmounts when the token is set, so
 * history.push from Login is unreliable — App consumes this on mount instead.
 *
 * A captured `/deeplink` always wins over Feed / Messages.
 */
export function queueStartupNavigation(value) {
  persistStartupScreen(value);
  const deeplink = captureIncomingDeeplink() || (getPendingDeeplink()
    ? persistDeeplink(getPendingDeeplink())
    : null);
  if (deeplink) return deeplink;

  const startup = getStartupRoute(value);
  writeSession(PENDING_PATH_KEY, startup.path);
  writeSession(PENDING_SIDEBAR_KEY, startup.sidebar);
  return startup;
}

export function consumeQueuedStartupNavigation() {
  try {
    const deeplink = getPendingDeeplink();
    const path = deeplink || readSession(PENDING_PATH_KEY);
    const sidebar = readSession(PENDING_SIDEBAR_KEY);
    sessionStorage.removeItem(PENDING_PATH_KEY);
    sessionStorage.removeItem(PENDING_SIDEBAR_KEY);
    clearStoredDeeplink();
    if (!path) return null;
    return {
      path,
      sidebar: sidebar || sidebarForPath(path),
    };
  } catch (e) {
    return null;
  }
}
